import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { ErrorMessage } from '@hookform/error-message';
import SizeEnum from '@models/enums/SizeEnum';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import { IFilter } from '@models/IFilterGroup';
import IFormComponent from '@models/IFormComponent';
import classNames from 'classnames';
import {
  DownshiftState,
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
} from 'downshift';
import { motion } from 'motion/react';
import { ChangeEvent, InputHTMLAttributes, useEffect } from 'react';

export interface ISelect
  extends InputHTMLAttributes<HTMLSelectElement>,
    IFormComponent {
  readonly dataTestId?: string;
  readonly displayLabel: boolean;
  readonly options: IFilter[];
  readonly placeholder: string;
  readonly name: string;
  readonly selectedItem?: IFilter | null;
  readonly inputSize?: SizeEnum;
  readonly onSelectedItemChange?: (newSelectedItem: IFilter | null) => void;
  readonly variant?: Exclude<VariantTypeEnum, VariantTypeEnum.Cta>;
  readonly containerClassName?: string;
  readonly fullWidthDropdown?: boolean;
  readonly selectStateReducer?: (
    state: UseSelectState<IFilter>,
    actionAndChanges: UseSelectStateChangeOptions<IFilter>
  ) => Partial<UseSelectState<IFilter>>;
  readonly dropdownClassName?: string;
}

const itemToString = (item: IFilter | null) => (item ? item.label : '');

const fallbackStateReducer = (
  _: DownshiftState<any>,
  actionAndChanges: UseSelectStateChangeOptions<any>
): Partial<UseSelectState<any>> => actionAndChanges.changes;

const Select = ({
  dataTestId,
  disabled,
  dropdownClassName,
  errors,
  label = '',
  name,
  onChange,
  options,
  placeholder,
  displayLabel,
  register,
  requiredErrorMessage,
  setValue,
  selectedItem: externalSelectedItem,
  onSelectedItemChange,
  inputSize = SizeEnum.Medium,
  variant,
  containerClassName = '',
  fullWidthDropdown = false,
  defaultValue,
  required,
  selectStateReducer = fallbackStateReducer,
}: ISelect) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect({
    items: options,
    itemToString,
    selectedItem: externalSelectedItem,
    stateReducer: selectStateReducer,
    onSelectedItemChange: ({ selectedItem }) => {
      setValue?.(name, String(selectedItem?.id ?? ''), {
        shouldValidate: true,
      });
      onChange?.({
        target: { value: String(selectedItem?.id ?? '') },
      } as ChangeEvent<HTMLSelectElement>);

      if (selectedItem) onSelectedItemChange?.(selectedItem);
    },
  });

  useEffect(() => {
    if (defaultValue) {
      const selectedOption =
        options.find((option) => option.id === defaultValue) ?? null;
      selectItem(selectedOption);
    }
  }, []);

  return (
    <div
      className={classNames(
        'tw-relative',
        containerClassName,
        inputSize === SizeEnum.Small ? 'tw-min-w-[146px]' : 'tw-min-w-[224px]',
        fullWidthDropdown && '!tw-min-w-[146px] md:!tw-min-w-[224px]'
      )}
    >
      <div>
        <input
          className="tw-peer tw-hidden"
          disabled={disabled}
          data-testid={dataTestId}
          {...register?.(name, { required: requiredErrorMessage })}
        />
        <label
          {...getLabelProps()}
          className={classNames(
            { 'tw-sr-only': !displayLabel },
            'tw-mb-xxs tw-block tw-font-sans tw-text-grey-70 peer-disabled:tw-text-grey-40',
            variant === VariantTypeEnum.Tertiary ? 'tw-text-base' : 'tw-text-xs'
          )}
        >
          {label ?? placeholder}
          {required ? ' *' : ''}
        </label>
        <div
          aria-label="toggle-select"
          className={classNames(
            'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-border tw-px-xs tw-outline-black hover:tw-border-black peer-disabled:tw-pointer-events-none peer-disabled:tw-border-grey-20 peer-disabled:tw-text-grey-40',
            {
              'tw-border-grey-60 tw-bg-transparent':
                variant === VariantTypeEnum.Primary,
              'tw-gap-1 tw-border-none tw-bg-transparent':
                variant === VariantTypeEnum.Secondary,
              'tw-border-grey-20 tw-bg-white tw-text-grey-70':
                variant !== VariantTypeEnum.Primary &&
                variant !== VariantTypeEnum.Secondary,
            },
            variant === VariantTypeEnum.Tertiary
              ? 'tw-border-grey-40 tw-py-2.5 tw-text-base md:tw-py-3.5'
              : 'tw-py-3.5 tw-text-sm',
            errors?.[name] && 'tw-border-red hover:tw-border-red'
          )}
          {...getToggleButtonProps()}
        >
          <Text
            variant={
              variant === VariantTypeEnum.Tertiary ? 'body' : 'body-small'
            }
            className={!selectedItem && placeholder ? 'tw-text-grey-70' : ''}
          >
            {selectedItem ? selectedItem.label : placeholder}
          </Text>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <Icon name="chevron-down" />
          </motion.div>
        </div>
      </div>
      <ul
        className={classNames(
          !isOpen && 'tw-hidden',
          fullWidthDropdown &&
            'tw-right-0 tw-w-full tw-border-grey-40 lg:tw-max-w-none',
          'tw-full tw-absolute tw-z-30 tw-mt-2 tw-max-h-52 tw-overflow-y-auto tw-rounded tw-border tw-border-grey-30 tw-bg-white tw-p-0 tw-shadow-md',
          dropdownClassName
        )}
        {...getMenuProps()}
      >
        {isOpen &&
          options.map((item, index) => (
            <li
              key={item.id}
              className={classNames(`tw-cursor-pointer tw-px-xs tw-py-[14px]`, {
                'tw-bg-grey-10':
                  highlightedIndex === index && selectedItem?.id !== item.id,
                'tw-bg-grey-20': selectedItem?.id === item.id,
              })}
              {...getItemProps({ item, index })}
            >
              <Text
                variant={
                  variant === VariantTypeEnum.Tertiary ? 'body' : 'body-small'
                }
              >
                {item.label}
              </Text>
            </li>
          ))}
      </ul>
      {requiredErrorMessage && errors && (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Text variant="label-12" className="tw-text-red">
              {message}
            </Text>
          )}
        />
      )}
    </div>
  );
};

export default Select;
